import { fromBase64Url, utf8 } from './encoding';
import type { HiType } from './constants';
import type { PracticeType } from './roles';
import { verifySignature } from './signatures';

/**
 * requested: waiting for the patient. approved: the patient signed it.
 * denied, revoked: the patient said no, or withdrew an approval.
 * expired: the request went unanswered, or the approval ran out.
 */
export const CONSENT_STATUSES = ['requested', 'approved', 'denied', 'revoked', 'expired'] as const;
export type ConsentStatus = (typeof CONSENT_STATUSES)[number];

/** ABDM purpose codes. Doctors ask for care management. More codes come with ABDM in phase 14. */
export const CONSENT_PURPOSES = ['CAREMGT'] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

/** Days a patient has to answer a request before it expires. */
export const CONSENT_REQUEST_TTL_DAYS = 7;
/** Longest an approval may last. */
export const CONSENT_MAX_DAYS = 365;
/** Most unanswered requests one practice may have open with one patient. */
export const MAX_OPEN_CONSENT_REQUESTS = 5;
/** Most source practices one approval may list. */
export const MAX_CONSENT_SOURCES = 20;

/** The terms a doctor asks for. Dates are ISO 8601 strings as the server returns them. */
export interface ConsentTerms {
  id: string;
  patientId: string;
  requesterPracticeId: string;
  requestedBy: string;
  hiTypes: HiType[];
  from: string;
  to: string;
  expiresAt: string;
  purpose: ConsentPurpose;
}

/** What the patient adds when approving: the signing device, which practices may send records, and when. */
export interface ConsentApprovalInput {
  deviceId: string;
  /** Practices that may send records besides the patient's own phone. */
  sources: string[];
  signedAt: string;
}

/**
 * What the patient's device signs to approve a consent: every term plus the
 * approval details, as JSON with fixed key order and sorted lists. Record
 * holders recompute these bytes and check the signature themselves before
 * sharing anything.
 */
export function consentApprovalBytes(terms: ConsentTerms, approval: ConsentApprovalInput): Uint8Array<ArrayBuffer> {
  const document = {
    v: 1,
    consent: terms.id,
    patient: terms.patientId,
    requesterPractice: terms.requesterPracticeId,
    requestedBy: terms.requestedBy,
    hiTypes: [...terms.hiTypes].sort(),
    from: terms.from,
    to: terms.to,
    expiresAt: terms.expiresAt,
    purpose: terms.purpose,
    sources: [...approval.sources].sort(),
    device: approval.deviceId,
    signedAt: approval.signedAt,
  };
  return utf8(`curentia/v1/consent-approval\0${JSON.stringify(document)}`);
}

/** A patient's approval as the server keeps it. */
export interface ConsentApproval extends ConsentApprovalInput {
  signature: string;
}

/** A practice named in a consent, for showing its name. */
export interface ConsentPractice {
  id: string;
  name: string;
  type: PracticeType;
  city: string;
}

/** A consent as the server returns it. */
export interface PublicConsent extends ConsentTerms {
  status: ConsentStatus;
  requestExpiresAt: string;
  createdAt: string;
  decidedAt: string | null;
  revokedAt: string | null;
  approval: ConsentApproval | null;
  /** The requesting practice first, then the source practices the patient named. */
  practices: ConsentPractice[];
  /** The name of the doctor who asked. */
  requestedByName: string;
}

/** The terms of a consent, exactly as the patient's device signs them. */
export function consentTermsOf(consent: ConsentTerms): ConsentTerms {
  return {
    id: consent.id,
    patientId: consent.patientId,
    requesterPracticeId: consent.requesterPracticeId,
    requestedBy: consent.requestedBy,
    hiTypes: [...consent.hiTypes],
    from: consent.from,
    to: consent.to,
    expiresAt: consent.expiresAt,
    purpose: consent.purpose,
  };
}

/**
 * True when the approval's signature checks out against the signing key of
 * the device that approved. Record holders run this before sharing or taking
 * in records, so an approval the server made up is caught.
 */
export async function approvalSignatureValid(
  consent: ConsentTerms & { approval: ConsentApproval | null },
  signingKey: Uint8Array,
): Promise<boolean> {
  if (!consent.approval) return false;
  const { deviceId, sources, signedAt, signature } = consent.approval;
  let bytes: Uint8Array;
  try {
    bytes = fromBase64Url(signature);
  } catch {
    return false;
  }
  return verifySignature(signingKey, bytes, consentApprovalBytes(consentTermsOf(consent), { deviceId, sources, signedAt }));
}

/** True when a document of this type, dated at this ISO time, falls within the consent's record types and dates. */
export function consentCovers(terms: Pick<ConsentTerms, 'hiTypes' | 'from' | 'to'>, hiType: HiType, date: string): boolean {
  const at = Date.parse(date);
  return terms.hiTypes.includes(hiType) && !Number.isNaN(at) && at >= Date.parse(terms.from) && at <= Date.parse(terms.to);
}
